import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import {
  BUY_SELL_ORDER_LINE_ITEMS_EDITABLE_STATUSES,
  type BuySellOrderStatus,
  buySellOrderInvoiceLock,
} from './buySellOrder';

/**
 * A BUY-SELL ORDER'S PRODUCTS — one row carries both trade sides (cost and price).
 *
 *   add · edit · delete ──▶ order window DRAFT..CLOSED ──▶ no open invoice ──▶ field rule
 *                                                                              buyPrice · unitsShipped:
 *                                                                              no open payable
 *
 * The bill facts are OPTIONAL: undefined = not judged. A bill page edits the lines its own open
 * invoice or payable bills, through the same mutation, so only the browser — which knows the host —
 * can apply them. The api judges the status window alone.
 */
export type BuySellOrderLineItemFacts = {
  buySellOrderStatus: BuySellOrderStatus;
  /** Any INVOICE bill on the order is open (`countOpenBuySellOrderBills`). Undefined = not judged. */
  hasOpenInvoice?: boolean;
  /** Any PAYABLE bill on the order is open (`countOpenBuySellOrderBills`). Undefined = not judged. */
  hasOpenPayable?: boolean;
};

export const BUY_SELL_ORDER_LINE_ITEM_FIELDS = [
  'product',
  'unitsOrdered',
  'unitsShipped',
  'unitsInvoiced',
  'buyPrice',
  'sellPrice',
  'deliveredPrice',
] as const;

export type BuySellOrderLineItemField = (typeof BUY_SELL_ORDER_LINE_ITEM_FIELDS)[number];

type LineAction = Action<BuySellOrderLineItemField>;

const decideEntity = (facts: BuySellOrderLineItemFacts, action: LineAction): Decision => {
  if (action.type === 'view') return allow();

  if (!BUY_SELL_ORDER_LINE_ITEMS_EDITABLE_STATUSES.includes(facts.buySellOrderStatus)) {
    return deny(
      'BUY_SELL_ORDER_LINE_ITEMS_LOCKED',
      `Products are locked once the buy-sell order is ${getStatusLabel(facts.buySellOrderStatus)}.`
    );
  }

  if (facts.hasOpenInvoice === true) return buySellOrderInvoiceLock();

  return allow();
};

/** The cost side follows the payable: an open one locks what it billed. */
const decideField = (facts: BuySellOrderLineItemFacts, field: BuySellOrderLineItemField): Decision => {
  switch (field) {
    case 'buyPrice':
    case 'unitsShipped':
      return facts.hasOpenPayable === true
        ? deny('BUY_SELL_ORDER_LINE_ITEM_PAYABLE_LOCKED', 'Locked while the payable is open.')
        : allow();
    case 'product':
    case 'unitsOrdered':
    case 'unitsInvoiced':
    case 'sellPrice':
    case 'deliveredPrice':
      return allow();
  }
};

const fieldRule = (facts: BuySellOrderLineItemFacts, action: LineAction): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  product: fieldRule,
  unitsOrdered: fieldRule,
  unitsShipped: fieldRule,
  unitsInvoiced: fieldRule,
  buyPrice: fieldRule,
  sellPrice: fieldRule,
  deliveredPrice: fieldRule,
} satisfies Record<BuySellOrderLineItemField, typeof fieldRule>;

export const buySellOrderLineItemPolicy: Policy<BuySellOrderLineItemFacts, BuySellOrderLineItemField> = definePolicy<
  BuySellOrderLineItemFacts,
  BuySellOrderLineItemField
>({
  kind: 'buy-sell order line',
  fields: BUY_SELL_ORDER_LINE_ITEM_FIELDS,
  ops: [],
  requires: {},
  entity: decideEntity,
  field: fieldRules,
});
